/*
 * Dockside reveal
 * Opens the hero photograph from a slit of light as the page scrolls.
 * The section is a tall scroll track; the stage inside it stays pinned while progress runs 0 → 1.
 */
(() => {
  if (customElements.get('dsi-reveal')) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const span = (p, start, end) => clamp((p - start) / (end - start), 0, 1);
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  class DsiReveal extends HTMLElement {
    constructor() {
      super();
      this.raf = null;
      this.active = false;
      this.current = 0;
      this.target = 0;
      this.tick = this.tick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMotionChange = this.setup.bind(this);
    }

    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.media = this.querySelector('[data-media]');
      this.img = this.querySelector('.dsi-hero__img');
      this.shade = this.querySelector('[data-shade]');
      this.hint = this.querySelector('[data-hint]');
      this.credit = this.querySelector('[data-credit]');

      this.gapFraction = parseFloat(this.dataset.gap);
      if (Number.isNaN(this.gapFraction)) this.gapFraction = 0.43;
      this.focalY = parseFloat(this.dataset.fy);
      if (Number.isNaN(this.focalY)) this.focalY = 0.6;

      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.motion.addEventListener) this.motion.addEventListener('change', this.onMotionChange);
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
      if (this.motion && this.motion.removeEventListener) {
        this.motion.removeEventListener('change', this.onMotionChange);
      }
    }

    setup() {
      this.teardown();
      const armed = document.documentElement.classList.contains('dsi-armed');
      if (this.motion.matches || !armed || !this.stage || !this.media) {
        document.documentElement.classList.remove('dsi-armed');
        this.clearStyles();
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      if (this.img && !this.img.complete) this.img.addEventListener('load', this.onResize, { once: true });

      this.active = true;
      this.measure();
      this.current = this.target = this.progress();
      this.render();
      this.classList.add('is-ready');
    }

    teardown() {
      if (!this.active) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.raf !== null) cancelAnimationFrame(this.raf);
      this.raf = null;
      this.active = false;
    }

    clearStyles() {
      [this.media, this.img, this.shade, this.hint, this.credit].forEach((el) => {
        if (el) el.removeAttribute('style');
      });
      this.classList.remove('is-ready');
    }

    onScroll() {
      this.target = this.progress();
      this.kick();
    }

    onResize() {
      this.measure();
      this.onScroll();
    }

    progress() {
      const rect = this.getBoundingClientRect();
      const range = rect.height - this.stage.offsetHeight;
      return range > 0 ? clamp(-rect.top / range, 0, 1) : 1;
    }

    // Where the gap under the pier lands on screen, given object-fit: cover and the vertical focus.
    measure() {
      const width = this.stage.clientWidth;
      const height = this.stage.clientHeight;
      const naturalWidth = (this.img && this.img.naturalWidth) || 1;
      const naturalHeight = (this.img && this.img.naturalHeight) || 1;
      const scale = Math.max(width / naturalWidth, height / naturalHeight);
      const drawnHeight = naturalHeight * scale;
      const gap = (height - drawnHeight) * this.focalY + this.gapFraction * drawnHeight;

      this.height = height;
      this.gap = clamp(gap, height * 0.12, height * 0.88);
      this.style.setProperty('--dsi-gap', `${this.gap.toFixed(1)}px`);
    }

    kick() {
      if (this.raf === null) this.raf = requestAnimationFrame(this.tick);
    }

    // A little inertia so the reveal follows the scroll smoothly instead of stepping with each wheel notch.
    tick() {
      this.raf = null;
      const delta = this.target - this.current;
      this.current = Math.abs(delta) < 0.0005 ? this.target : this.current + delta * 0.2;
      this.render();
      if (this.current !== this.target) this.kick();
    }

    render() {
      const p = this.current;

      const open = easeInOut(span(p, 0.04, 0.78));
      const top = Math.max(0, (this.gap - 1) * (1 - open));
      const bottom = Math.max(0, (this.height - this.gap - 1) * (1 - open));
      this.media.style.clipPath = `inset(${top.toFixed(1)}px 0 ${bottom.toFixed(1)}px 0)`;

      if (this.img) {
        const settle = easeOut(span(p, 0, 0.95));
        this.img.style.transform = `scale(${(1.12 - 0.12 * settle).toFixed(4)})`;
      }
      if (this.shade) {
        this.shade.style.opacity = (0.6 * (1 - easeOut(span(p, 0.15, 0.9)))).toFixed(3);
      }
      if (this.hint) this.hint.style.opacity = (1 - span(p, 0, 0.06)).toFixed(3);
      if (this.credit) this.credit.style.opacity = span(p, 0.85, 1).toFixed(3);
    }
  }

  customElements.define('dsi-reveal', DsiReveal);
})();
